/**
 * File drop zones ([data-dropzone]) around a real <input type="file" multiple>.
 *
 * Files can be dropped on the zone or picked with the input, which stays focusable,
 * so Enter or Space opens the file picker. Every pick adds to the list instead of
 * replacing it; each listed file has a remove button. Files of another type, larger
 * than the limit or beyond the maximum count are refused with a message read out by
 * screen readers. The input's own FileList is rebuilt (DataTransfer) after every
 * change, so the server receives exactly the files on the list.
 *
 * Settings and messages come from the markup: data-max-files, data-max-bytes and
 * data-msg-type / data-msg-size / data-msg-count / data-label-remove, where ":name"
 * and ":max" are filled in here.
 */
const canRebuild = (() => {
    try {
        return typeof DataTransfer === 'function' && !!new DataTransfer().items;
    } catch (e) {
        return false;
    }
})();

function formatSize(bytes) {
    if (bytes >= 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
    }
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function initDropzone(zone) {
    const input = zone.querySelector('input[type="file"]');
    const list = zone.querySelector('[data-dropzone-list]');
    const message = zone.querySelector('[data-dropzone-message]');
    if (!input || !list) {
        return;
    }
    const maxFiles = Number(zone.dataset.maxFiles) || 10;
    const maxBytes = Number(zone.dataset.maxBytes) || 10 * 1024 * 1024;
    const accepted = (input.getAttribute('accept') || '').split(',').map((ext) => ext.trim().toLowerCase()).filter(Boolean);
    const text = (key, values = {}) => Object.entries(values)
        .reduce((out, [k, v]) => out.split(`:${k}`).join(String(v)), zone.dataset[key] || '');

    // Without JavaScript the plain file input shows; with it, the drop area stands in
    // for it on screen while the input stays the keyboard and screen-reader control.
    const area = zone.querySelector('[data-dropzone-area]');
    zone.classList.add('is-enhanced');
    if (area) {
        area.hidden = false;
        area.addEventListener('click', () => {
            if (!input.disabled) {
                input.click();
            }
        });
    }

    let files = [];

    const say = (lines) => {
        if (message) {
            message.textContent = lines.join(' ');
        }
    };

    const sync = () => {
        if (canRebuild) {
            const transfer = new DataTransfer();
            files.forEach((file) => transfer.items.add(file));
            input.files = transfer.files;
        }
        render();
    };

    function render() {
        list.replaceChildren();
        files.forEach((file, index) => {
            const item = document.createElement('li');
            item.className = 'ui-dropzone__file';
            const name = document.createElement('span');
            name.className = 'ui-dropzone__name';
            name.textContent = file.name;
            const size = document.createElement('span');
            size.className = 'ui-dropzone__size';
            size.textContent = formatSize(file.size);
            item.append(name, size);
            if (canRebuild) {
                const remove = document.createElement('button');
                remove.type = 'button';
                remove.className = 'ui-dropzone__remove';
                remove.setAttribute('aria-label', text('labelRemove', { name: file.name }));
                remove.textContent = '×';
                remove.addEventListener('click', () => {
                    files.splice(index, 1);
                    say([]);
                    sync();
                    const buttons = list.querySelectorAll('.ui-dropzone__remove');
                    (buttons[Math.min(index, buttons.length - 1)] || input).focus();
                });
                item.append(remove);
            }
            list.append(item);
        });
        zone.classList.toggle('has-files', files.length > 0);
    }

    const accepts = (file) => {
        if (!accepted.length) {
            return true;
        }
        const dot = file.name.lastIndexOf('.');
        return dot > 0 && accepted.includes(file.name.slice(dot).toLowerCase());
    };

    const add = (incoming) => {
        const problems = [];
        const known = new Set(files.map((file) => `${file.name}|${file.size}|${file.lastModified}`));
        let full = false;
        incoming.forEach((file) => {
            if (!accepts(file)) {
                problems.push(text('msgType', { name: file.name }));
            } else if (file.size > maxBytes) {
                problems.push(text('msgSize', { name: file.name, max: formatSize(maxBytes) }));
            } else if (!known.has(`${file.name}|${file.size}|${file.lastModified}`)) {
                if (files.length >= maxFiles) {
                    full = true;
                } else {
                    files.push(file);
                    known.add(`${file.name}|${file.size}|${file.lastModified}`);
                }
            }
        });
        if (full) {
            problems.push(text('msgCount', { max: maxFiles }));
        }
        say(problems);
        sync();
    };

    input.addEventListener('change', () => {
        const picked = [...(input.files || [])];
        if (canRebuild) {
            add(picked);
            return;
        }
        // Without DataTransfer the input keeps only the last pick: check it as a whole.
        files = [];
        add(picked);
        if (files.length !== picked.length) {
            input.value = '';
            files = [];
            render();
        }
    });

    ['dragenter', 'dragover'].forEach((type) => zone.addEventListener(type, (event) => {
        if (input.disabled || !event.dataTransfer || ![...event.dataTransfer.types].includes('Files')) {
            return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        zone.classList.add('is-dragover');
    }));
    ['dragleave', 'dragend'].forEach((type) => zone.addEventListener(type, (event) => {
        if (!zone.contains(event.relatedTarget)) {
            zone.classList.remove('is-dragover');
        }
    }));
    zone.addEventListener('drop', (event) => {
        zone.classList.remove('is-dragover');
        if (input.disabled || !event.dataTransfer?.files?.length) {
            return;
        }
        event.preventDefault();
        if (canRebuild) {
            add([...event.dataTransfer.files]);
        } else {
            say([zone.dataset.msgDrop || '']);
        }
    });
}

export function initDropzones(root = document) {
    root.querySelectorAll('[data-dropzone]:not([data-dropzone-ready])').forEach((zone) => {
        zone.dataset.dropzoneReady = '1';
        initDropzone(zone);
    });
}
